const EXTERNAL_URL_REGEX = /^(http:|https:)?\/\//i;

/**
 * Determines whether the url is external or internal url. e.g. http/s.
 *
 * @param url to check whether is external or not.
 */
export function isExternalUrl(url: string): boolean {
	return EXTERNAL_URL_REGEX.test(url);
}

const PROTOCOL_DELIMITER_REGEXES = [/:?$/, /(:\/\/)?$/];
const PROTOCOL_DELIMITER = "://";
const TRAILING_SLASH_REGEX = /(\/$)|(\/(\?))/;

/**
 * Remove the trailing slash to support SEO guidelines
 *
 * @param url to clean up
 */
export function removeTrailingSlash(url: string): string {
	const baseUrl = url.split("?")[0];
	const shouldCleanUp = baseUrl[0] !== "/" || (baseUrl.match(/\//g) || []).length > 1;
	return shouldCleanUp ? url.replace(TRAILING_SLASH_REGEX, "$3") : url;
}

/**
 * Determines whether url has a supported protocol in it
 *
 * @param url to check whether it is in list of supported protocols
 * @param protocols an array of supported protocols.
 */
export function isTrustedProtocolUrl(url: string | undefined, protocols: string[] | undefined): boolean {
	if (!url || !protocols) {
		return false;
	}

	return protocols.some((protocol) =>
		url.startsWith(
			`${PROTOCOL_DELIMITER_REGEXES.reduce(
				(protocolValue, regex) => protocolValue.replace(regex, ""),
				protocol
			)}${PROTOCOL_DELIMITER}`
		)
	);
}

export function removeSlugsFromUrl(url: string, ...slugs: string[]): string {
	const newSlugs = url.split("/").filter((slug) => slug && !slugs.includes(slug));
	return `/${newSlugs.join("/")}`;
}

export function updateQueryStringParameter(
	url: string,
	key: string,
	value: string | undefined | null,
	addIfNotExists = true
): string {
	const re = new RegExp(`([?&])?${key}=.*?(&|$)`, "ig");

	const replaceMultAmps = (input: string) => input.replace(/&{1,}/g, "&").replace("?&", "?");

	if (url.match(re)) {
		const updated =
			value === null || value === undefined ? url.replace(re, `$1$2`) : url.replace(re, `$1${key}=${value}$2`);
		return replaceMultAmps(updated);
	}

	if (!addIfNotExists) {
		return url;
	}

	const separator = url.indexOf("?") !== -1 ? "&" : "?";
	return replaceMultAmps(`${url}${separator}${key}=${value}`);
}

/**
 * Replaces all occurances of specified parameters with provided values.
 *
 * @param url to process
 * @param parameters to process. For parameters where value undefined or null is specified, parameters are removed from url.
 * Parameters which don't exist in url are added to the url by default. This can be changed with addIfNotExists parameter
 * @param addIfNotExists specifies if parameters which exist in url should be added or not
 */
export function updateQueryStringParameters(
	url: string,
	parameters: Record<string, string | undefined | null>,
	addIfNotExists = true
): string {
	return Object.getOwnPropertyNames(parameters).reduce(
		(currentUrl, key) => updateQueryStringParameter(currentUrl, key, parameters[key], addIfNotExists),
		url
	);
}
